import { Injectable } from '@angular/core';
import { SearchResultsModel } from '../models/search-results-model';
import { ResultSelectionService } from './result-selection.service';
import { environment } from 'src/environments/environment';
import { BehaviorSubject } from 'rxjs';
import { QueryStateService } from './query-state.service';
import { PubSearchService } from './pub-search.service';
import { SearchOutputModel } from '../models/search-output-model';
import { MessageService } from 'primeng/api';

@Injectable({
  providedIn: 'root'
})
export class ZipDownloadService {

  constructor(private selectionSvc: ResultSelectionService,
    private queryStateSvc: QueryStateService,
    private searchSvc: PubSearchService,
    private messageSvc: MessageService) {

  }

  private isDownloadingSubject = new BehaviorSubject<boolean>(false);
  public isDownloading$ = this.isDownloadingSubject.asObservable();

  private downloadedItems: SearchResultsModel[] = [];
  private batchSize_max = 100; // this is used for the query to get the items for select all
  private pageCount = 0;

  setIsDownloading(data: boolean) {
    this.isDownloadingSubject.next(data);
  }

  getFiles(): void {
    // Reset items to download
    this.downloadedItems = [];
    this.pageCount = 0;

    // Get selected files and ensure we can get all the items based on the selection.
    //All Excpet should be true if isAllSelected is true.
    if (this.selectionSvc.isAllSelected() || this.selectionSvc.isAllExcept()) {
      this.ensureAllData();
    }
    else {
      this.downloadedItems = this.selectionSvc.getSelected();
      this.downloadSelectedDocuments();
    }
  }

  // Fetch all the items needed to execute the file downloads
  ensureAllData() {
    const max = environment.download_files_max;
    let batchSize = this.calcRemainingBatchSize();
    let downloadItemCount = this.downloadedItems.length;

    let tc = this.selectionSvc.getCount();

    // Limit the download to the max items
    if (tc > max) {
      tc = max;
    }

    let sr = this.queryStateSvc.getUpdatedSearchRequest();
    sr.size = batchSize;
    sr.skip = this.batchSize_max * this.pageCount;
    sr.filters?.push({ field: 'IsLegacy', value: 'No' }); // Filter out the legacy items when fetching the downloads.
    // console.log('dwnld search req: ' + JSON.stringify(sr));

    // if (!this.getCancelDownload()) {
    if (downloadItemCount < tc || sr.skip < downloadItemCount) {
      //get the next batch of data
      this.searchSvc.downloadItems(sr).subscribe((data: SearchOutputModel) => {
        if (data.results) {
          this.downloadedItems = this.downloadedItems.concat(data.results);
          this.filterExceptions();
          this.pageCount++;
        }

        this.ensureAllData();
      });
    }
    else {
      this.downloadSelectedDocuments();
    }
  }

  calcRemainingBatchSize(): number {
    const downloadItemCount = this.downloadedItems.length;
    const tc = this.selectionSvc.getCount();
    const max = environment.download_files_max;
    const remainder = max - downloadItemCount;
    let batchSize = this.batchSize_max;

    if ((downloadItemCount < tc) && (remainder < this.batchSize_max)) {
      batchSize = remainder;
    }

    return batchSize;
  }

  filterExceptions() {
    let ex = this.selectionSvc.getExcluded();
    this.downloadedItems = this.downloadedItems.filter(item => !ex.some(e => e.document.AccessionNumber == item.document.AccessionNumber));
  }

  downloadSelectedDocuments() {
    // remove the legacy items
    this.downloadedItems = this.downloadedItems.filter(i => i.document.ItemType != 'legacy');

    let items = this.createDownloadItemsList();
    this.selectionSvc.setDownloadableCount(items.length);

    const worker = new Worker(new URL('assets/workers/download-worker.js', environment.download_worker_host));

    worker.postMessage({ items });

    worker.onmessage = (event) => {
      const message = event.data;
      switch (message.type) {
        case 'save':
          this.saveFile(message.blob, message.fileName);
          break;
        case 'error':
          this.setIsDownloading(false);
          this.messageSvc.add({ key: 'downloadFiles', severity: 'error', summary: 'There was an error Downloading Files' });
          // console.error(message.message);
          break;
        case 'complete':
          this.setIsDownloading(false);
          if (items.length < 1) {
            this.messageSvc.add({ key: 'downloadFiles', severity: 'error', summary: 'No Files to Download!', detail: 'Only Legacy items were Selected!' });
          } else {
            this.messageSvc.add({ key: 'downloadFiles', severity: 'success', summary: 'Download Complete!', detail: 'Files are in your downloads folder!' });
          }
          break;
      }
    };

    worker.onerror = (error) => {
      console.error('Web Worker error: ', error.message);
    };
  }

  private saveFile(blob: Blob, fileName: string): void {
    const link = document.createElement('a');
    const url = window.URL.createObjectURL(blob);
    link.href = url;
    link.download = fileName;
    link.click();
    window.URL.revokeObjectURL(url);
  }

  private createDownloadItemsList(): any[] {
    const items = this.downloadedItems.map(item => ({
      accessionNumber: item.document.AccessionNumber,
      url: item.document.Url
    }));

    return items;
  }

}
